import { useEffect, useState } from "react";
import { useSelectOptions } from "../../../popups/Wizard/helpers/useSelectOptions";

import type { SelectOptionT } from "../../../models/models";

const productionLocationSO = [
  { value: "local", label: "Local" },
  { value: "abroad", label: "Abroad" },
];

/**
 * @param isActive whether the user has actually engaged with the search box (focused or clicked it).
 * Until then the lookup lists are NOT fetched — see the comment on the useSelectOptions call below.
 */
const useAutocomplete = (
  isActive = false
): [
  SelectOptionT[],
  (value: SelectOptionT) => void,
  SelectOptionT,
  (value: SelectOptionT) => void
] => {
  const [autocompleteList, setAutocompleteList] = useState<SelectOptionT[]>([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedAutocomplete, setSelectedAutocomplete] = useState(null);
  // Do not fetch the lookup lists until the user actually engages with the search box. <Search
  // withAutocomplete /> mounts on every /campaigns and /deliveries load, and this hook used to pull
  // the full advertiser, brand, client and industry tables — ~1.68 MB — for a dropdown most users
  // never open. Spec 34 D1.
  //
  // `selectedCategory` alone is NOT a sufficient gate: useCategory restores a category on mount from
  // the filterType query param or from the per-user lastSelectedCategoryValue in localStorage
  // (written by every search), and FloatingSearchForm defaults to categories[0] on tablet/mobile. So
  // `isActive` — set from a real focus/pointer interaction with the search area by the caller — is
  // what keeps the cold load clean; the category is still required because the list cannot be
  // populated without one.
  const {
    advertisersSO,
    brandsSO,
    industrySO,
    productionCompaniesSO,
    postProductionCompaniesSO,
    advertisingAgenciesSO,
    mediaCompaniesAllSO,
    clientsSO,
    broadcastersSO,
    isFetchingSelectOptions,
  } = useSelectOptions({ skip: !isActive || !selectedCategory });

  useEffect(() => {
    if (selectedCategory && !isFetchingSelectOptions && clientsSO?.length) {
      switch (selectedCategory) {
        case "advertiser":
        case "advertiserId": {
          setAutocompleteList(advertisersSO);
          break;
        }
        case "advertisingagency": {
          setAutocompleteList(advertisingAgenciesSO);
          break;
        }
        case "brand": {
          setAutocompleteList(brandsSO);
          break;
        }
        case "broadcaster": {
          setAutocompleteList(broadcastersSO);
          break;
        }
        case "mediacompany": {
          setAutocompleteList(mediaCompaniesAllSO);
          break;
        }
        case "postproduction": {
          setAutocompleteList(postProductionCompaniesSO);
          break;
        }
        case "supplier": {
          setAutocompleteList(productionCompaniesSO);
          break;
        }
        case "productionlocation": {
          setAutocompleteList(productionLocationSO);
          break;
        }
        case "industry": {
          setAutocompleteList(industrySO);
          break;
        }
        default: {
          setAutocompleteList([]);
        }
      }
    }
  }, [selectedCategory, isFetchingSelectOptions, clientsSO]);

  const changeList = (category: SelectOptionT) => {
    setSelectedCategory(category?.value);
  };

  return [
    autocompleteList,
    changeList,
    selectedAutocomplete,
    setSelectedAutocomplete,
  ];
};

export default useAutocomplete;
